/**
 * When a release happens while a tab is passive, the loaded app references
 * hashed chunk filenames that no longer exist on the server. The next lazy route
 * load then 404s with "Failed to fetch dynamically imported module".
 */

import {
  CHUNK_LOAD_ERROR_PATTERN,
  RELOAD_ATTEMPT_KEY,
  RELOAD_RETRY_WINDOW_MS,
  FALLBACK_ELEMENT_ID,
} from '../types/constants';
import { ChunkReloadDeps } from '../types/helper.types';

export function isChunkLoadError(error: unknown): boolean {
  if (!error) {
    return false;
  }

  const candidate = error as { name?: unknown; message?: unknown };
  if (candidate.name === 'ChunkLoadError') {
    return true;
  }

  const message = typeof error === 'string' ? error : typeof candidate.message === 'string' ? candidate.message : '';
  return CHUNK_LOAD_ERROR_PATTERN.test(message);
}

export function attemptChunkReload(deps: ChunkReloadDeps = {}): boolean {
  const reload = deps.reload ?? (() => window.location.reload());
  const now = deps.now ?? (() => Date.now());

  try {
    const storage = deps.storage ?? sessionStorage;
    const lastAttempt = Number(storage.getItem(RELOAD_ATTEMPT_KEY));
    const reloadedRecently = !!lastAttempt && now() - lastAttempt < RELOAD_RETRY_WINDOW_MS;
    if (reloadedRecently) {
      return false;
    }

    storage.setItem(RELOAD_ATTEMPT_KEY, String(now()));
  } catch {
    // Storage unavailable (private mode / sandboxed iframe / quota) — we can't guard against a
    // reload loop, so degrade to the manual prompt rather than risk an unguarded reload.
    return false;
  }

  reload();
  return true;
}

export function renderChunkLoadFallback(targetDocument: Document = document): void {
  if (targetDocument.getElementById(FALLBACK_ELEMENT_ID)) {
    return;
  }

  const container = targetDocument.createElement('div');
  container.id = FALLBACK_ELEMENT_ID;
  container.setAttribute(
    'style',
    'position:fixed;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;' +
      'background:#ffffff;color:#1a1a1a;font-family:"Plus Jakarta Sans",sans-serif;font-size:16px;text-align:center;padding:24px;z-index:2147483647;'
  );

  const message = targetDocument.createElement('p');
  message.textContent = 'A new version is available. Please refresh the page to continue.';
  message.setAttribute('style', 'margin:0;max-width:420px;');

  const button = targetDocument.createElement('button');
  button.type = 'button';
  button.textContent = 'Refresh';
  button.setAttribute(
    'style',
    'cursor:pointer;border:none;border-radius:8px;padding:10px 20px;font:inherit;background:#1a73e8;color:#ffffff;'
  );
  button.addEventListener('click', () => window.location.reload());

  container.append(message, button);
  targetDocument.body.append(container);
}
